import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/article';
import { categories } from '@/mocks/category';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => getArticleBySlug(id || ''), [id]);
  const related = useMemo(() => getRelatedArticles(id || '', article.categorySlug), [id, article.categorySlug]);
  const category = useMemo(() => categories.find((c) => c.slug === article.categorySlug), [article.categorySlug]);

  const [copied, setCopied] = useState(false);
  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    image: article.heroImage,
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    author: { '@type': 'Organization', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜网',
      logo: { '@type': 'ImageObject', url: 'https://51chigua.com/logo.png' },
    },
    description: article.summary,
    articleBody: article.content.join(' '),
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51chigua.com/' },
        {
          '@type': 'ListItem',
          position: 2,
          name: category?.name || article.category,
          item: `https://51chigua.com/category/${article.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: `https://51chigua.com/article/${article.slug}`,
        },
      ],
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        {/* 文章头图 */}
        <section className="relative w-full min-h-[420px] md:min-h-[520px] flex items-end overflow-hidden">
          <img
            src={article.heroImage}
            alt={article.title}
            title={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
          <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-10 md:pb-14">
            <Breadcrumb
              items={[
                { label: category?.name || article.category, href: `/category/${article.categorySlug}` },
                { label: article.title, href: `/article/${article.slug}` },
              ]}
            />
            <h1 className="mt-4 font-heading text-2xl md:text-4xl lg:text-5xl font-black text-background-50 max-w-3xl leading-tight">
              {article.title}
            </h1>
            <div className="mt-4 flex items-center gap-4 flex-wrap text-sm text-foreground-300">
              <span>
                <i className="ri-user-line mr-1" />
                {article.author}
              </span>
              <span>
                <i className="ri-calendar-line mr-1" />
                {article.publishDate.split('T')[0]}
              </span>
              <span>
                <i className="ri-eye-line mr-1" />
                {article.viewCount}
              </span>
            </div>
          </div>
        </section>

        {/* 文章内容 */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <article className="max-w-3xl mx-auto">
            <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-8 font-medium border-l-4 border-accent-500 pl-4">
              {article.summary}
            </p>
            {article.content.map((paragraph, i) => (
              <p key={i} className="text-base text-foreground-700 leading-relaxed mb-5">
                {paragraph}
              </p>
            ))}

            <div className="mt-8 pt-6 border-t border-background-200/60">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium text-secondary-700 bg-secondary-100 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                onClick={handleShare}
                className="px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-line mr-2" />
                {copied ? '已复制链接' : '分享爆料'}
              </button>
              <button className="px-6 py-3 text-sm font-semibold text-foreground-700 border-2 border-foreground-300/40 rounded-full hover:border-primary-400 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-bookmark-line mr-2" />
                收藏文章
              </button>
            </div>
          </article>
        </section>

        {/* 来源与数据 */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10">
            <div className="flex-1">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">爆料来源</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {article.source} · 所有内容均经过多方交叉验证
              </p>
              <div className="mt-6 flex items-center gap-8">
                <div>
                  <p className="text-3xl md:text-4xl font-black text-primary-500">{article.viewCount}</p>
                  <p className="text-xs text-foreground-500 mt-1">阅读量</p>
                </div>
                <div>
                  <p className="text-3xl md:text-4xl font-black text-accent-500">{article.shareCount}</p>
                  <p className="text-xs text-foreground-500 mt-1">转发量</p>
                </div>
              </div>
            </div>
            <div className="lg:w-[380px]">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 text-xs font-medium text-accent-700 bg-background-50/90 backdrop-blur-sm rounded-full whitespace-nowrap">
                    {article.category}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-6">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group block bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-accent-300/40 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={rel.heroImage}
                        alt={rel.title}
                        title={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-xs font-medium text-accent-600">{rel.category}</span>
                      <h4 className="mt-1 font-heading text-base font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}